import React from 'react';
import { Helmet } from 'react-helmet-async';
import CtaStrip from '../components/CtaStrip';
import Footer from '../components/Footer';
import StatCounter from '../components/StatCounter';

const MODULE_ACCENT_COLORS = ['var(--ms-blue)', 'var(--ms-teal)', 'var(--ms-purple)', 'var(--ms-orange)'];
const BADGE_STYLES = [
  { color:'var(--ms-blue)', background:'var(--ms-blue-pale)' },
  { color:'var(--ms-teal)', background:'#DFF6F0' },
  { color:'var(--ms-purple)', background:'#F4F0FF' },
  { color:'var(--ms-orange)', background:'#FFF1EC' },
];

const modules = [
  { name:'Sales & Customer Engagement', badge:'Expert' },
  { name:'Finance & Operations', badge:'Expert' },
  { name:'Power Platform & PVA', badge:'Expert' },
  { name:'Field Service & Marketing', badge:'Proficient' },
];

const reviews = [
  { initials:'JT', color:'var(--ms-blue)', name:'James T.', role:'Head of Operations · United Kingdom',
    text:'"Exceptional work on our D365 CE plugin. Delivered exactly to spec, on time, with clean and well-documented code. Will absolutely hire again."' },
  { initials:'SM', color:'var(--ms-teal)', name:'Sarah M.', role:'CRM Manager · Australia',
    text:'"Built a PVA chatbot that transformed our customer service. Handles 70% of inbound queries automatically. Professional, thorough, and fast."' },
  { initials:'ML', color:'var(--ms-purple)', name:'Marco L.', role:'IT Director · Netherlands',
    text:'"Our D365 Finance data migration was handled perfectly. Zero errors, fast turnaround. This is the consultant you want for serious enterprise work."' },
];

const teaserServices = [
  { icon:'🔌', title:'Plugin Development', desc:'Server-side C# plugins for business logic, triggered by D365 events with full SDK access.', accent:'var(--ms-blue)' },
  { icon:'🤖', title:'PVA Chatbot Development', desc:'Intelligent customer service chatbots built on Power Virtual Agents, fully integrated with D365.', accent:'var(--ms-teal)' },
  { icon:'🛠️', title:'CRM Support & Maintenance', desc:'Ongoing support, bug fixes, and enhancements to keep your D365 environment running smoothly.', accent:'var(--ms-purple)' },
];

const specialisations = [
  '🔌 Plugin Dev', '📜 Web Resources', '⚡ Power Automate', '🧩 PCF Controls', '🤖 PVA Chatbots', '🔗 API Integration',
];

export default function HomePage({ onNavigate }) {
  return (
    <div>
      <Helmet>
        <title>AdjustD365 | Dynamics 365 Developer &amp; Consultant for Hire</title>
        <meta name="description" content="Hire an experienced Dynamics 365 developer and consultant. Specialising in D365 plugin development, PCF controls, Power Automate, PVA chatbots and CRM integrations. Fast turnaround, enterprise quality." />
        <link rel="canonical" href="https://adjustd365.com/" />
      </Helmet>
      {/* HERO */}
      <div className="section-pad" style={{
        padding:'80px 48px 72px',
        background:'linear-gradient(135deg, #004578 0%, #0078D4 55%, #0091E0 100%)',
        position:'relative', overflow:'hidden',
      }}>
        {/* Grid pattern overlay */}
        <div style={{
          position:'absolute', inset:0, opacity:0.04, pointerEvents:'none',
          backgroundImage:'repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 0,transparent 50%),repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 0,transparent 50%)',
          backgroundSize:'32px 32px',
        }} />

        <div className="hero-grid" style={{ maxWidth:'1200px', margin:'0 auto', display:'grid', gridTemplateColumns:'1.1fr 0.9fr', gap:'80px', alignItems:'center', position:'relative' }}>
          {/* Left */}
          <div>
            <div style={{
              display:'inline-flex', alignItems:'center', gap:'8px',
              background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.25)',
              borderRadius:'20px', padding:'6px 14px', fontSize:'12px', fontWeight:'600',
              letterSpacing:'0.06em', textTransform:'uppercase', color:'rgba(255,255,255,0.9)', marginBottom:'24px',
            }}>
              <div style={{ width:'6px', height:'6px', borderRadius:'50%', background:'#7FBA00' }} />
              Microsoft Dynamics 365 Specialist
            </div>

            <h1 style={{ fontSize:'clamp(30px,3.5vw,50px)', fontWeight:700, lineHeight:1.1, letterSpacing:'-0.02em', color:'#fff', marginBottom:'20px' }}>
              Extend D365 to work exactly the way your business does
            </h1>
            <p style={{ fontSize:'17px', fontWeight:300, color:'rgba(255,255,255,0.8)', lineHeight:1.7, marginBottom:'36px', maxWidth:'500px' }}>
              Custom plugins, Power Automate flows, PCF controls, PVA chatbots, and integrations — built by an experienced D365 consultant who delivers enterprise-grade quality.
            </p>
            <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
              <button onClick={() => onNavigate('contact')} style={{ background:'#fff', color:'var(--ms-blue)', padding:'12px 28px', borderRadius:'4px', fontSize:'14px', fontWeight:'600', border:'none' }}>
                Get in Touch
              </button>
              <button onClick={() => onNavigate('services')} style={{ background:'transparent', color:'#fff', padding:'12px 28px', borderRadius:'4px', fontSize:'14px', fontWeight:'600', border:'1px solid rgba(255,255,255,0.5)' }}>
                View Services
              </button>
            </div>
          </div>

          {/* Right — card */}
          <div style={{ background:'#fff', borderRadius:'6px', padding:'28px', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:'11px', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text-s)', marginBottom:'18px' }}>
              D365 Modules Covered
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'20px' }}>
              {modules.map((m, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'var(--bg-2)', borderRadius:'4px', borderLeft:`3px solid ${MODULE_ACCENT_COLORS[i]}` }}>
                  <span style={{ fontSize:'13px', fontWeight:600, color:'var(--text-h)' }}>{m.name}</span>
                  <span style={{ fontSize:'11px', fontWeight:600, padding:'2px 8px', borderRadius:'10px', ...BADGE_STYLES[i] }}>{m.badge}</span>
                </div>
              ))}
            </div>
            {/* Stats */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', background:'var(--border)', gap:'1px', border:'1px solid var(--border)', borderRadius:'4px', overflow:'hidden' }}>
              <div style={{ background:'#fff', padding:'12px' }}>
                <StatCounter target={96} suffix="+" label="Happy Customers" duration={1600} />
              </div>
              <div style={{ background:'#fff', padding:'12px' }}>
                <StatCounter staticVal="★ 5.0" label="Rated" />
              </div>
              <div style={{ background:'#fff', padding:'12px' }}>
                <StatCounter target={8} suffix="+ yrs" label="D365 Experience" duration={1200} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TRUST BAR */}
      <div className="section-pad" style={{ background:'var(--ms-blue-darker)', padding:'20px 48px' }}>
        <div style={{ maxWidth:'1200px', margin:'0 auto', display:'flex', alignItems:'center', gap:'40px', flexWrap:'wrap' }}>
          <span style={{ fontSize:'12px', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(255,255,255,0.5)', whiteSpace:'nowrap' }}>
            Specialisations
          </span>
          <div style={{ display:'flex', gap:'12px', flexWrap:'wrap', alignItems:'center' }}>
            {specialisations.map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div style={{ width:'1px', height:'14px', background:'rgba(255,255,255,0.2)' }} />}
                <span style={{ fontSize:'13px', fontWeight:500, color:'rgba(255,255,255,0.85)' }}>{s}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <section className="section-pad" style={{ padding:'80px 48px' }}>
        <div style={{ maxWidth:'1200px', margin:'0 auto' }}>
          <div style={{ fontSize:'12px', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--ms-blue)', marginBottom:'10px' }}>Client Feedback</div>
          <h2 style={{ fontSize:'clamp(24px,2.8vw,36px)', fontWeight:700, letterSpacing:'-0.02em', color:'var(--text-h)', marginBottom:'8px' }}>Trusted by businesses across industries</h2>
          <p style={{ fontSize:'15px', color:'var(--text-m)', marginBottom:'44px' }}>Real feedback from clients who've worked with me on D365 projects.</p>
          <div className="grid-3col" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'20px' }}>
            {reviews.map((r, i) => (
              <div key={i} style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:'6px', padding:'28px' }}>
                <div style={{ marginBottom:'16px' }}>
                  <span style={{ color:'var(--ms-yellow)', fontSize:'13px', letterSpacing:'1px' }}>★★★★★</span>
                </div>
                <p style={{ fontSize:'14px', color:'var(--text-m)', lineHeight:1.7, fontStyle:'italic', marginBottom:'20px', borderLeft:'3px solid var(--ms-blue-pale)', paddingLeft:'14px' }}>{r.text}</p>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:r.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:700, color:'#fff', flexShrink:0 }}>{r.initials}</div>
                  <div>
                    <div style={{ fontSize:'14px', fontWeight:600, color:'var(--text-h)' }}>{r.name}</div>
                    <div style={{ fontSize:'12px', color:'var(--text-s)' }}>{r.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="section-pad" style={{ padding:'80px 48px', background:'var(--bg-2)', borderTop:'1px solid var(--border)' }}>
        <div style={{ maxWidth:'1200px', margin:'0 auto' }}>
          <div style={{ fontSize:'12px', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--ms-blue)', marginBottom:'10px' }}>What I Do</div>
          <h2 style={{ fontSize:'clamp(24px,2.8vw,36px)', fontWeight:700, letterSpacing:'-0.02em', color:'var(--text-h)', marginBottom:'32px' }}>Core D365 customisation services</h2>
          <div className="grid-3col-tight" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1px', background:'var(--border-2)', border:'1px solid var(--border-2)', borderRadius:'6px', overflow:'hidden' }}>
            {teaserServices.map((s, i) => (
              <div
                key={i}
                onClick={() => onNavigate('services')}
                style={{ background:'#fff', padding:'32px', cursor:'pointer', position:'relative' }}
              >
                <div style={{ position:'absolute', top:0, left:0, right:0, height:'3px', background:s.accent }} />
                <span style={{ fontSize:'26px', marginBottom:'16px', display:'block' }}>{s.icon}</span>
                <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-h)', marginBottom:'8px' }}>{s.title}</div>
                <p style={{ fontSize:'14px', color:'var(--text-m)', lineHeight:1.6, marginBottom:'16px' }}>{s.desc}</p>
                <span style={{ fontSize:'13px', fontWeight:600, color:'var(--ms-blue)' }}>View all services →</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaStrip
        heading="Have a D365 requirement? Let's talk."
        subtext="Submit your query and I'll get back to you within 24 hours with a clear plan."
        buttonLabel="Get in Touch"
        onAction={() => onNavigate('contact')}
      />
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
